import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Stack,
  Typography,
  Chip,
  Avatar,
  Divider,
  Button,
} from "@mui/material";
import { NotificationsNoneOutlined, NotificationsActive } from "@mui/icons-material";
import { axiosPrivate } from "../../axios/axios";
import { masterAPI } from "../Masters/dataConfig";
import { timeAgo } from "../../utils/timeAgo";
import { stripHtml } from "../../utils/stripHtml";
import { getNotificationColor } from "../../utils/notificationVisual";
import { getNotificationRoute } from "../../utils/notificationRoutes";

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationItems = useSelector((state) => state.notification.items);
  const unreadCount = useSelector((state) => state.notification.unreadCount);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    const getAllLink = masterAPI["notificationLog"].getall;
    axiosPrivate
      .get(`${getAllLink}`)
      .then((res) => {
        dispatch({ type: "setNotifications", payload: res.data.result });
      })
      .catch((e) => {
        console.log(e, "NotificationLog getall error");
      });
  };

  const handleClose = () => setAnchorEl(null);

  const handleItemClick = (notif) => {
    handleClose();

    if (!notif.read) {
      const bulkLink = masterAPI["notificationLog"].bulkupdate;
      axiosPrivate
        .put(`${bulkLink}`, [notif.id], { skipSuccessToast: true })
        .then(() => {
          dispatch({ type: "markNotificationRead", payload: notif.id });
        })
        .catch((e) => {
          console.log(e, "NotificationLog bulkupdate error");
        });
    }

    const route = getNotificationRoute(notif.notificationType, notif.referenceId);
    if (route) navigate(route);
  };

  const handleViewAll = () => {
    handleClose();
    navigate("/notifications");
  };

  return (
    <>
      <Badge
        badgeContent={unreadCount}
        color="secondary"
        sx={{ ".css-1l8s8lm-MuiBadge-badge": { color: "#2362B4" }, cursor: "pointer" }}
        onClick={handleOpen}
      >
        <NotificationsNoneOutlined sx={{ color: "white" }} />
      </Badge>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 360 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", letterSpacing: 0.5 }}>
              NOTIFICATIONS
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} New`}
                size="small"
                sx={{ backgroundColor: "#E15554", color: "#fff", fontWeight: 600 }}
              />
            )}
          </Stack>
          <Divider />

          <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
            {notificationItems.length === 0 ? (
              <Typography sx={{ p: 2.5, textAlign: "center", color: "#6C757D", fontSize: "0.85rem" }}>
                No notifications yet
              </Typography>
            ) : (
              notificationItems.slice(0, 10).map((notif) => (
                <Box
                  key={notif.id}
                  onClick={() => handleItemClick(notif)}
                  sx={{
                    display: "flex",
                    gap: 1.2,
                    px: 2,
                    py: 1.2,
                    cursor: "pointer",
                    backgroundColor: notif.read ? "transparent" : "#F5F9FE",
                    "&:hover": { backgroundColor: "#F0F3F7" },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: getNotificationColor(notif.category),
                      width: 34,
                      height: 34,
                      mt: 0.3,
                    }}
                  >
                    <NotificationsActive fontSize="small" />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: notif.read ? 500 : 700, fontSize: "0.85rem" }}>
                      {notif.title}
                    </Typography>
                    <Typography variant="body2" noWrap sx={{ color: "#495057", fontSize: "0.8rem" }}>
                      {stripHtml(notif.message)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#ADB5BD" }}>
                      {timeAgo(notif.createdOn)}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          <Divider />
          <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
            <Button size="small" onClick={handleViewAll} sx={{ textTransform: "none" }}>
              View all notifications
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default NotificationDropdown;
